
import React from 'react';
import { Hourglass, Box, Bike, Truck, CookingPot, Pause, Timer, BrainCircuit, Repeat2, Lock, Snowflake, Layers, CheckCircle2, UtensilsCrossed, ShoppingBag, Eye, HelpCircle, Wind, CalendarClock } from 'lucide-react';
import { Order, SystemConfig, SafeDispatchAnalysis } from '../../types';
import { BaseTooltip } from '../shared/BaseTooltip';
import { AuditDictionary } from '../../services/logistics/core/auditCalculations';
import { getMinutesUntilClose } from '../../services/logistics/core/timing';
import { getMinutesUntilScheduledStart, isScheduledRoutingEligible } from '../../services/logistics/core/scheduled';

interface Props {
    order: Order;
    safeDispatchLimit: number;
    adjustedTravelTime: number;
    elapsedMinutes: number;
    isWaitingPickup: boolean;
    isThirdParty: boolean;
    isBoomerangReserved?: boolean | undefined;
    isHeld?: boolean | undefined;
    isCritical: boolean;
    isHighRisk: boolean;
    isSafe: boolean;
    isSolitary?: boolean;
    systemConfig?: SystemConfig;
    analysis?: SafeDispatchAnalysis; // v58.3: Pass analysis object to avoid duplicated logic
    ultraCompact?: boolean; // v60.0: Modo ultra-compacto para telas pequenas
}

/**
 * v46.1: OrderBadges - High-Performance Tactical Feedback
 * - Rótulos explícitos para Cozinha (Retenção) e Motoboy (Bumerangue).
 * - Feedback visual de Drenagem e sincronia com a Sofia.
 */
export const OrderBadges: React.FC<Props> = ({
    order, safeDispatchLimit, adjustedTravelTime, elapsedMinutes,
    isWaitingPickup, isThirdParty, isBoomerangReserved, isHeld, isCritical, isHighRisk, isSafe, isSolitary,
    systemConfig, analysis,
    ultraCompact = false
}) => {

    const isSolar = systemConfig?.isSolarMode;

    // Helper para badges ultra-compactos
    const renderCompactBadge = (icon: React.ReactNode, bgClass: string, tooltip: string) => (
        <BaseTooltip data={{ title: tooltip, description: tooltip, type: 'time' }}>
            <div className={`flex items-center justify-center w-5 h-5 rounded transition-all duration-300 ${bgClass}`}>
                {icon}
            </div>
        </BaseTooltip>
    );

    if (order.isLoading) {
        if (ultraCompact) {
            return renderCompactBadge(
                <BrainCircuit className="w-3 h-3 animate-spin" />,
                isSolar ? 'bg-status-info/20 text-status-info' : 'bg-status-info/20 text-status-info',
                'Analisando'
            );
        }
        return (
            <div className={`flex items-center gap-1 font-black text-xs px-1.5 py-0.5 rounded uppercase-ops whitespace-nowrap ${isSolar ? 'bg-status-info/20 text-status-info' : 'bg-status-info/20 text-status-info'}`}>
                <BrainCircuit className="w-3 h-3 animate-spin" />
                <span>Analisando</span>
            </div>
        );
    }

    if (order.status === 'waiting_confirmation') {
        if (ultraCompact) {
            return renderCompactBadge(
                <HelpCircle className="w-3 h-3" />,
                isSolar ? 'bg-status-pending text-white shadow-md' : 'bg-status-pending/20 text-status-pending',
                'Confirmar pedido'
            );
        }
        return (
            <div className={`flex items-center gap-1 font-black text-xs px-2 py-0.5 rounded uppercase-ops whitespace-nowrap ${isSolar ? 'bg-status-pending text-white shadow-md' : 'bg-status-pending/20 text-status-pending'}`}>
                <HelpCircle className="w-3 h-3" />
                <span>CONFIRMAR?</span>
            </div>
        );
    }

    if (order.type === 'TAKEOUT') {
        if (order.isReady) {
            if (ultraCompact) {
                return renderCompactBadge(
                    <CheckCircle2 className="w-3 h-3" />,
                    'bg-status-ready text-white shadow-lg animate-bounce',
                    'Pronto para retirada'
                );
            }
            return (
                <div className={`flex items-center gap-1 font-black text-xs px-2 py-0.5 rounded uppercase-ops whitespace-nowrap text-white shadow-lg animate-bounce bg-status-ready`}>
                    <CheckCircle2 className="w-3 h-3" />
                    <span>PRONTO P/ RETIRADA</span>
                </div>
            );
        }
        if (ultraCompact) {
            return renderCompactBadge(
                <UtensilsCrossed className="w-3 h-3" />,
                isSolar ? 'bg-status-pending text-white shadow-sm' : 'bg-status-pending/20 text-status-pending',
                'Na cozinha'
            );
        }
        return (
            <div className={`flex items-center gap-1 font-black text-xs px-2 py-0.5 rounded uppercase-ops whitespace-nowrap ${isSolar ? 'bg-status-pending text-white shadow-sm' : 'bg-status-pending/20 text-status-pending'}`}>
                <UtensilsCrossed className="w-3 h-3" />
                <span>NA COZINHA</span>
            </div>
        );
    }

    const scheduledLeadMinutes = systemConfig?.cardapioWeb?.scheduledRoutingLeadMinutes ?? 45;
    const isScheduledLocked = !isScheduledRoutingEligible(order, scheduledLeadMinutes);
    const minutesToWindow = getMinutesUntilScheduledStart(order);

    if (isScheduledLocked && minutesToWindow !== null) {
        const hours = Math.floor(minutesToWindow / 60);
        const mins = Math.max(0, minutesToWindow % 60);
        const waitLabel = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;

        if (ultraCompact) {
            return renderCompactBadge(
                <CalendarClock className="w-3 h-3" />,
                isSolar ? 'bg-status-info text-white shadow-sm' : 'bg-status-info/10 text-status-info',
                `Agendado - libera em ${waitLabel}`
            );
        }
        return (
            <BaseTooltip data={{ title: 'Pedido Agendado', description: `Roteirizacao sera liberada em ${waitLabel}.`, type: 'time' }}>
                <div className={`flex items-center gap-1 font-black text-xs px-2 py-0.5 rounded uppercase-ops whitespace-nowrap ${isSolar ? 'bg-status-info text-white shadow-sm' : 'bg-status-info/10 text-status-info'}`}>
                    <CalendarClock className="w-3 h-3" />
                    <span>AGENDADO {waitLabel}</span>
                </div>
            </BaseTooltip>
        );
    }

    const totalDeadline = order.timeLimit || 45;
    let badgeLabel = `${totalDeadline} → ${safeDispatchLimit} min`;
    let Icon = Hourglass;
    let bgBadge = isSafe ? 'bg-status-ready/10 text-status-ready' : 'bg-status-pending/10 text-status-pending';

    const isOwn = !isThirdParty;
    const acceptedStrategies = order.acceptedStrategies || [];
    const isRetentionConfirmed = acceptedStrategies.includes('production_retention') || acceptedStrategies.includes('dispatch_hold');
    const boomerangPhase = order.boomerangPhase;

    // v58.3: Use values from analysis object if available, otherwise fallback to local calculation
    const tolerance = analysis ? analysis.tolerance : (isOwn ? (systemConfig?.fleetConfig.slaToleranceMinutes || 15) : (systemConfig?.thirdPartyConfig.slaToleranceMinutes || 7));
    const serviceTime = analysis ? analysis.service : (isOwn ? (systemConfig?.fleetConfig.serviceTimeMinutes || 5) : (systemConfig?.thirdPartyConfig.serviceTimeMinutes || 7));
    const kitchenRemaining = analysis ? analysis.kitchen : (!order.isReady && order.status !== 'ready' ? Math.max(0, (systemConfig?.averageKitchenTime || 17) - elapsedMinutes) : 0);

    let auditData = AuditDictionary.safeDispatch(totalDeadline, elapsedMinutes, adjustedTravelTime, tolerance, kitchenRemaining, serviceTime);

    // Prioridade 1: DRENAGEM (Feedback do kill-switch de fechamento)
    if (systemConfig) {
        const minsToClose = getMinutesUntilClose(systemConfig);
        if (minsToClose < 25 && !['delivered', 'archived', 'canceled'].includes(order.status)) {
            bgBadge = 'bg-status-alert text-white font-black ring-1 ring-white/30 shadow-lg';
            Icon = Wind;
            badgeLabel = "DRENAGEM";
            auditData = { title: "Drenagem Ativa", description: "Fechamento do hub próximo. Sofia desabilitou as travas de eficiência para limpar o balcão.", type: 'alert' };
        }
    }

    // Prioridade 2: Estados Críticos ou Estratégicos
    if (isCritical && badgeLabel !== "DRENAGEM") {
        bgBadge = 'bg-status-critical text-white font-black shadow-lg';
        Icon = Timer;
    } else if (isHighRisk && badgeLabel !== "DRENAGEM") {
        bgBadge = 'bg-status-warning text-white font-bold shadow-md';
        Icon = Timer;
    } else if (isBoomerangReserved) {
        bgBadge = 'bg-status-info/20 text-status-info font-black';
        Icon = Repeat2;
        if (boomerangPhase === 'return') {
            badgeLabel = `${safeDispatchLimit}m • BUMER VOLTA`;
            auditData = { title: "Estratégia Bumerangue (VOLTA)", description: "Pedido aguardando retorno do motoboy para segunda perna confirmada.", type: 'logic' };
        } else {
            badgeLabel = `${safeDispatchLimit}m • BUMER VAI`;
            auditData = { title: "Estratégia Bumerangue (VAI)", description: "Pedido confirmado para primeira perna do bumerangue.", type: 'logic' };
        }
    }

    // Prioridade 3: Status Logísticos e Retenções
    if (order.status === 'assigned' && order.driverId) {
        if (isWaitingPickup) {
            Icon = Box;
            badgeLabel = "COLETA";
            auditData = { title: "Aguardando Coleta", description: "Motoboy mobilizado. O tempo de saída segura expirou e a viagem iniciou.", type: 'alert' };
            bgBadge = 'bg-status-info text-white shadow-lg';
        } else {
            Icon = Bike;
            badgeLabel = "EM ROTA";
            auditData = { title: "Em Trânsito", description: `Previsão de chegada no cliente em ${adjustedTravelTime} minutos.`, type: 'time' };
            bgBadge = 'bg-status-ready text-white font-black shadow-lg';
        }
    } else if (isThirdParty) {
        Icon = Truck;
        // v46.5: Mantém o cronômetro completo mesmo em envios externos para visibilidade do despachante
        auditData = { title: "Despacho Externo", description: "Entrega delegada para aplicativo parceiro. Prazo sob gestão do provedor.", type: 'logic' };
        // Ajuste de contraste para Solar nos badges de SLA
        if (isSolar && badgeLabel.includes('→')) {
            if (isSafe) bgBadge = 'bg-status-ready/20 text-status-ready';
            else bgBadge = 'bg-status-pending/20 text-status-pending';
        }
    } else if (isHeld) {
        if (order.isReady) {
            Icon = Layers;
            badgeLabel = isRetentionConfirmed ? `${safeDispatchLimit}m • RETIDO CONF` : `${safeDispatchLimit}m • RETIDO`;
            bgBadge = isSolar ? 'bg-status-warning text-white font-black shadow-sm' : 'bg-status-warning/20 text-status-warning';
            auditData = {
                title: isRetentionConfirmed ? "Despacho Retido Confirmado" : "Despacho Retido",
                description: isRetentionConfirmed
                    ? "Estratégia confirmada pelo despachante. Pedido aguardando janela ideal de saída."
                    : "Pedido pronto, mas Sofia aguarda vizinhos de cozinha para maximizar economia.",
                type: 'financial'
            };
        } else {
            Icon = Snowflake;
            badgeLabel = isRetentionConfirmed ? `${safeDispatchLimit}m • ESPERA CONF` : `${safeDispatchLimit}m • ESPERAR`;
            bgBadge = isSolar ? 'bg-status-info text-white font-black shadow-sm' : 'bg-status-info/20 text-status-info';
            auditData = {
                title: isRetentionConfirmed ? "Retenção de Produção Confirmada" : "Retenção de Produção",
                description: isRetentionConfirmed
                    ? "Estratégia confirmada: pedido em espera controlada até melhor janela logística."
                    : "Sugerido à cozinha: NÃO INICIAR. Pedido com folga de prazo aguardando par logístico.",
                type: 'logic'
            };
        }
    } else if (isSolitary) {
        Icon = Eye;
        badgeLabel = `${safeDispatchLimit}m • SOFIA`;
        bgBadge = isSolar ? 'bg-slate-200 text-slate-700' : 'bg-card-bg border border-border-dim text-text-muted';
        auditData = { title: "Monitoramento Sofia", description: "Pedido isolado e com prazo folgado. Sofia aguarda oportunidade de agrupamento.", type: 'time' };
    }

    // v60.0: Versão ultra-compacta - maior para mostrar texto legível do safe dispatch
    // Badge maior com tempo visível (ex: "5m", "12m")
    if (ultraCompact) {
        // Formata o label do badge para modo ultra-compacto
        const compactLabel = badgeLabel.length > 12 
            ? (badgeLabel.includes('→') ? badgeLabel.split('→')[1].trim() : badgeLabel.slice(0, 4))
            : badgeLabel;
        
        return (
            <BaseTooltip data={auditData}>
                <div className={`flex items-center justify-center h-7 px-1.5 rounded transition-all duration-300 font-black text-[10px] tracking-tight ${bgBadge}`}>
                    <Icon className="w-3 h-3 fill-current shrink-0 mr-1" />
                    <span className="leading-none">{compactLabel}</span>
                </div>
            </BaseTooltip>
        );
    }

    return (
        <BaseTooltip data={auditData}>
            <div className={`flex items-center gap-1.5 font-black text-xs px-2 py-0.5 rounded uppercase-ops whitespace-nowrap transition-all duration-300 ${bgBadge}`}>
                <Icon className={`w-3 h-3 fill-current`} />
                <span className="tracking-tighter">{badgeLabel}</span>
            </div>
        </BaseTooltip>
    );
};
